import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Download, FileText, Loader2, Newspaper } from 'lucide-react';
import { Card } from '@shared/ui/containers/Card';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { pageCopy } from '@shared/lib/pageCopy';
import { toApiError } from '@shared/api/errors';
import { toast } from '@shared/ui/feedback/Toast';
import { fetchNewsStand, fetchIssueDownloadUrl, type NewsIssue } from './api';
import { formatRelative } from '@shared/lib/format';

/**
 * Inside-a-newsstand view. Lists the published **issues** of a
 * publication, newest first. Each article opens an in-app PDF preview
 * backed by the signed `/download` URL, with download still available
 * from the viewer. The header mirrors the rest of the app (HeroHeader
 * with a back affordance) for visual consistency.
 */
export function NewsStandDetail(): JSX.Element {
  const { newsstandId } = useParams<{ newsstandId: string }>();
  const navigate = useNavigate();
  const copy = pageCopy().newsStand;
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [hiddenIssueIds, setHiddenIssueIds] = useState<string[]>([]);

  useEffect(() => {
    setHiddenIssueIds([]);
  }, [newsstandId]);

  const standQuery = useQuery({
    queryKey: ['news-stand', newsstandId],
    queryFn: () => fetchNewsStand(newsstandId ?? ''),
    enabled: !!newsstandId,
  });

  const stand = standQuery.data?.stand;
  const issues = (standQuery.data?.issues ?? []).filter(
    (issue) => !hiddenIssueIds.includes(issue.article_id),
  );

  const handleOpen = async (issue: NewsIssue): Promise<void> => {
    if (downloadingId) return;
    setDownloadingId(issue.article_id);
    try {
      const url = await fetchIssueDownloadUrl(issue.newsstand_id, issue.article_id);
      const params = new URLSearchParams({
        title: issue.name,
        ...(issue.file_name ? { file: issue.file_name } : {}),
      });
      navigate(
        `/app/news-stands/${issue.newsstand_id}/articles/${issue.article_id}/view?${params.toString()}`,
        {
          state: {
            pdfUrl: url,
            title: issue.name,
            fileName: issue.file_name,
          },
        },
      );
    } catch (e) {
      const apiError = toApiError(e);
      if (apiError.status === 404) {
        setHiddenIssueIds((current) =>
          current.includes(issue.article_id) ? current : [...current, issue.article_id],
        );
        toast.danger("Couldn't open article", 'This article is no longer available.');
        return;
      }
      toast.danger("Couldn't open article", e instanceof Error ? e.message : 'Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={stand?.name ?? copy.title}
        subtitle={stand?.description ?? copy.subtitle}
        backTo="/app/news-stands"
        backBehavior="parent"
      />

      <div className="flex flex-col gap-4 px-5 pt-4 pb-safe-or-6">
        {standQuery.isLoading ? (
          <>
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </>
        ) : standQuery.isError ? (
          <ErrorState onRetry={() => standQuery.refetch()} />
        ) : issues.length === 0 ? (
          <EmptyState
            icon={<Newspaper className="h-7 w-7" strokeWidth={1.5} />}
            title="Nothing yet."
            description="Check back soon — new news lands here."
          />
        ) : (
          issues.map((issue) => (
            <IssueCard
              key={issue.article_id}
              issue={issue}
              busy={downloadingId === issue.article_id}
              disabled={!!downloadingId}
              onOpen={() => void handleOpen(issue)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function IssueCard({
  issue,
  busy,
  disabled,
  onOpen,
}: {
  issue: NewsIssue;
  busy: boolean;
  disabled: boolean;
  onOpen: () => void;
}): JSX.Element {
  const [showCover, setShowCover] = useState(Boolean(issue.cover_url));

  return (
    <Card asChild noGrain className="overflow-hidden p-0 disabled:opacity-60">
      <button type="button" onClick={onOpen} disabled={disabled} className="block w-full text-left">
        {showCover && issue.cover_url ? (
          <img
            src={issue.cover_url}
            alt=""
            className="h-40 w-full object-cover"
            onError={() => setShowCover(false)}
          />
        ) : null}
        <div className="flex items-center gap-3 p-4">
          {!showCover || !issue.cover_url ? (
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
              <FileText className="h-5 w-5" strokeWidth={1.5} />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <p className="text-caption text-ink-muted flex items-center gap-2">
              <Calendar className="h-3 w-3" strokeWidth={1.5} />
              {issue.published_at ? formatRelative(issue.published_at) : 'Recently published'}
            </p>
            <h2 className="font-display text-display-lg text-ink leading-tight mt-1 truncate">
              {issue.name}
            </h2>
            {issue.description ? (
              <p className="text-body text-ink-secondary mt-1 line-clamp-2">{issue.description}</p>
            ) : null}
          </div>
          {busy ? (
            <Loader2 className="h-5 w-5 shrink-0 animate-spin text-ink-muted" strokeWidth={1.75} />
          ) : (
            <Download className="h-5 w-5 shrink-0 text-ink-muted" strokeWidth={1.75} />
          )}
        </div>
      </button>
    </Card>
  );
}
